import { memo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { classNames } from '~/utils/classNames';
import type { TaskItem } from '~/types/context';

interface TaskProgressProps {
  tasks: TaskItem[];
  /** When true, the panel is docked onto the composer as one unit: its bottom
   *  corners go square so it reads as a header attached to the input below. */
  attached?: boolean;
}

export const TaskProgress = memo(({ tasks, attached = false }: TaskProgressProps) => {
  const [expanded, setExpanded] = useState(true);

  const doneCount = tasks.filter((t) => t.status === 'done').length;
  const failedCount = tasks.filter((t) => t.status === 'failed').length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.15 }}
      className={classNames('rounded-lg border border-hairline bg-surface px-3.5 py-2.5', attached && 'rounded-b-none')}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <span className="ui-caption">Plan</span>
        <span className="text-muted text-[10px]">·</span>
        <span className="text-muted text-[11px] tabular-nums">
          {doneCount} / {tasks.length} done
        </span>
        <button type="button" onClick={() => setExpanded(!expanded)} className="ml-auto ui-btn-ghost">
          {expanded ? 'collapse' : 'expand'}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col max-h-[260px] overflow-y-auto modern-scrollbar">
              {tasks.map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
              {failedCount > 0 && (
                <div className="mt-1.5 text-[11px] text-danger">
                  {failedCount} task{failedCount === 1 ? '' : 's'} failed
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

function TaskRow({ task }: { task: TaskItem }) {
  const isDone = task.status === 'done';
  const isRunning = task.status === 'in-progress';
  const isFailed = task.status === 'failed';

  return (
    <div className="flex items-start gap-2.5 py-[5px] text-[12.5px]">
      <span
        className={classNames(
          'w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 mt-[3px]',
          isDone && 'border-success bg-success/[0.12]',
          isRunning && 'border-accent',
          isFailed && 'border-danger bg-danger/[0.12]',
          !isDone && !isRunning && !isFailed && 'border-prominent',
        )}
      >
        {isDone && <span className="i-ph:check text-success text-[10px]" />}
        {isRunning && <span className="ui-dot ui-dot-live ui-pulsing !w-1.5 !h-1.5" />}
        {isFailed && <span className="i-ph:x text-danger text-[10px]" />}
      </span>
      <div className="min-w-0 flex-1">
        <span
          className={classNames(
            isDone && 'text-secondary line-through decoration-faint',
            isRunning && 'text-primary',
            isFailed && 'text-danger',
            !isDone && !isRunning && !isFailed && 'text-muted',
          )}
        >
          {task.title}
        </span>
        {/* Keep it minimal: title + at most a 2-line description. `task.details` can be a
            long wall of internal build reasoning, so clamp it rather than dumping it all. */}
        {task.details && <p className="text-faint mt-0.5 text-[11px] leading-snug line-clamp-2">{task.details}</p>}
      </div>
    </div>
  );
}
